import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Target, Users, TrendingUp, Sparkles, Plus, Trophy } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "new", label: "New", color: "border-slate-300", accent: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "qualified", label: "Qualified", color: "border-sky-400", accent: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "proposal", label: "Proposal", color: "border-amber-400", accent: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "negotiation", label: "Negotiation", color: "border-secondary", accent: "bg-orange-100 text-orange-800 border-orange-200" },
  { key: "won", label: "Won", color: "border-emerald-500", accent: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "lost", label: "Lost", color: "border-red-400", accent: "bg-red-100 text-red-800 border-red-200" },
];

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [scoring, setScoring] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const load = async () => {
    const r = await api.get("/leads");
    setLeads(r.data);
  };
  useEffect(() => { load(); }, []);

  const pipeline = leads.filter((l) => l.stage !== "won" && l.stage !== "lost")
    .reduce((s, l) => s + Number(l.estimated_value || 0), 0);
  const won = leads.filter((l) => l.stage === "won").reduce((s, l) => s + Number(l.estimated_value || 0), 0);
  const open = leads.filter((l) => l.stage !== "won" && l.stage !== "lost").length;
  const conversion = leads.length ? ((leads.filter((l) => l.stage === "won").length / leads.length) * 100) : 0;

  const moveStage = async (leadId, stage) => {
    // Optimistic
    setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, stage } : l));
    try {
      await api.patch(`/leads/${leadId}`, { stage });
      toast.success(`Moved to ${STAGES.find((s) => s.key === stage)?.label || stage}`);
    } catch {
      toast.error("Move failed"); load();
    }
  };

  const scoreLead = async (leadId) => {
    setScoring(leadId);
    try {
      const r = await api.post(`/ai/score-lead/${leadId}`);
      toast.success(`Score: ${r.data.score} — ${r.data.reason}`);
      load();
    } catch { toast.error("Scoring failed"); }
    finally { setScoring(null); }
  };

  const onDragStart = (leadId) => (e) => {
    setDraggingId(leadId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", leadId);
  };
  const onDragEnd = () => { setDraggingId(null); setDragOverStage(null); };
  const onDragOver = (stageKey) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };
  const onDrop = (stageKey) => (e) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain") || draggingId;
    setDragOverStage(null); setDraggingId(null);
    if (!leadId) return;
    const lead = leads.find((l) => l.lead_id === leadId);
    if (!lead || lead.stage === stageKey) return;
    moveStage(leadId, stageKey);
  };

  return (
    <div className="space-y-6" data-testid="crm-root">
      <SectionHeader
        title="CRM · Sales pipeline"
        subtitle="Drag any card between stages to move it — updates are optimistic & saved live"
        right={<NewLeadDialog onCreated={load} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="crm-open" label="Open leads" value={open} icon={Users} />
        <KPI testId="crm-pipeline" label="Pipeline value" value={pipeline} compact icon={Target} tone="warn" />
        <KPI testId="crm-won" label="Won this cycle" value={won} compact icon={Trophy} tone="good" />
        <KPI testId="crm-conv" label="Conversion" value={conversion.toFixed(1)} suffix="%" icon={TrendingUp} />
      </div>

      {/* Drag-and-drop Kanban board */}
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="crm-kanban">
        {STAGES.map((stg) => {
          const stageLeads = leads.filter((l) => l.stage === stg.key);
          const stageValue = stageLeads.reduce((s, l) => s + Number(l.estimated_value || 0), 0);
          const isOver = dragOverStage === stg.key;
          return (
            <div
              key={stg.key}
              className="w-72 shrink-0"
              data-testid={`kanban-col-${stg.key}`}
              onDragOver={onDragOver(stg.key)}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (dragOverStage === stg.key) setDragOverStage(null);
              }}
              onDrop={onDrop(stg.key)}
            >
              <div className={`bg-card border rounded-md h-full ${stg.color} border-t-4 transition-all ${isOver ? "ring-2 ring-primary ring-offset-1 bg-primary/5" : ""}`}>
                <div className="p-3 border-b flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold font-display">{stg.label}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{stageLeads.length} · {inr(stageValue, { compact: true })}</div>
                  </div>
                  <Badge className={`${stg.accent} text-[10px]`}>{stageLeads.length}</Badge>
                </div>
                <div className="p-2 space-y-2 max-h-[560px] overflow-y-auto min-h-[100px]">
                  {stageLeads.map((l) => (
                    <div
                      key={l.lead_id}
                      draggable
                      onDragStart={onDragStart(l.lead_id)}
                      onDragEnd={onDragEnd}
                      className={`border rounded p-2.5 bg-background hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${draggingId === l.lead_id ? "opacity-40 rotate-2 scale-95" : ""}`}
                      data-testid={`lead-card-${l.lead_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{l.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.company}</div>
                        </div>
                        {l.score > 0 && (
                          <Badge className={`text-[10px] shrink-0 ${l.score >= 75 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : l.score >= 50 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                            {l.score}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 font-semibold text-sm">{inr(l.estimated_value, { compact: true })}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5">{l.source}</Badge>
                        <span className="truncate">{l.owner}</span>
                      </div>
                      <div className="mt-2 flex gap-1">
                        <Select value={l.stage} onValueChange={(v) => moveStage(l.lead_id, v)}>
                          <SelectTrigger data-testid={`move-${l.lead_id}`} className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm" variant="outline"
                          disabled={scoring === l.lead_id}
                          data-testid={`score-${l.lead_id}`}
                          onClick={() => scoreLead(l.lead_id)}
                          className="h-7 px-2" title="AI score"
                        >
                          <Sparkles className="h-3 w-3 text-secondary" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className={`text-center text-xs py-8 border border-dashed rounded-md ${isOver ? "border-primary text-primary" : "border-muted text-muted-foreground"}`}>
                      {isOver ? "Drop here" : "No leads"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Leads table */}
      <Card className="shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold font-display">All leads · list view</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                <th className="py-2 font-medium">Name</th>
                <th className="py-2 font-medium">Company</th>
                <th className="py-2 font-medium">Contact</th>
                <th className="py-2 font-medium">Source</th>
                <th className="py-2 font-medium">Owner</th>
                <th className="py-2 font-medium text-right">Value</th>
                <th className="py-2 font-medium text-center">Score</th>
                <th className="py-2 font-medium">Stage</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.lead_id} className="border-b hover:bg-accent/50">
                  <td className="py-2 font-medium">{l.name}</td>
                  <td className="py-2">{l.company}</td>
                  <td className="py-2 text-xs">{l.email}<br/><span className="text-muted-foreground font-mono">{l.phone}</span></td>
                  <td className="py-2"><Badge variant="outline">{l.source}</Badge></td>
                  <td className="py-2 text-xs">{l.owner}</td>
                  <td className="py-2 cell-num font-medium">{inr(l.estimated_value, { compact: true })}</td>
                  <td className="py-2 text-center">
                    <Badge className={l.score >= 75 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : l.score >= 50 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                      {l.score || "—"}
                    </Badge>
                  </td>
                  <td className="py-2"><Badge variant="outline">{l.stage}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewLeadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", source: "referral",
    estimated_value: 0, stage: "new", owner: "", notes: "",
  });
  const save = async () => {
    try {
      await api.post("/leads", { ...form, estimated_value: Number(form.estimated_value) });
      toast.success("Lead added"); setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-lead-btn" className="h-9 bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-1" />New lead</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
          <Field label="Company"><Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></Field>
          <Field label="Source">
            <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["referral","website","event","cold","ad","partner"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Estimated value (₹)"><Input type="number" value={form.estimated_value} onChange={(e) => setForm({...form, estimated_value: e.target.value})} /></Field>
          <Field label="Owner"><Input value={form.owner} onChange={(e) => setForm({...form, owner: e.target.value})} /></Field>
          <Field label="Stage">
            <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Notes" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button data-testid="lead-save" onClick={save} className="bg-primary hover:bg-primary/90">Add lead</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
